import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VVS_LOGO = 'https://customer-assets.emergentagent.com/job_f891e7ec-6e5d-4f56-adc7-3ef2bb466a63/artifacts/hntth0xe_white%20vvs%20logo.png';

// Preload range: 5 photos ahead, 2 behind
const PRELOAD_AHEAD = 5;
const PRELOAD_BEHIND = 2;

// Individual gallery image with spinner
function GalleryImage({ img, index, onClick }) {
  const [loaded, setLoaded] = useState(false);
  
  return (
    <div
      onClick={onClick}
      style={{
        aspectRatio: '1',
        cursor: 'pointer',
        overflow: 'hidden',
        background: '#0d1f3c',
        position: 'relative',
      }}
      data-testid={`gallery-image-${index}`}
    >
      {/* Spinner while loading */}
      {!loaded && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0d1f3c',
        }}>
          <img
            src={VVS_LOGO}
            alt=""
            style={{
              width: '24px',
              height: '24px',
              animation: 'vvs-spin 1.5s ease-in-out infinite',
            }}
          />
        </div>
      )}
      {/* Image */}
      <img
        src={img.thumb}
        alt={`WITHIN ${index + 1}`}
        onLoad={() => setLoaded(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: loaded ? 'block' : 'none',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      />
    </div>
  );
}

export default function WithinGalleryPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [closeVisible, setCloseVisible] = useState(false);
  const preloadCache = useRef({});

  useEffect(() => {
    document.title = 'WITHIN Gallery // VVS';
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gallery/within`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setImages(data.images || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Aggressive preload: N-2 to N+5
  const preloadLightboxRange = useCallback((centerIndex) => {
    const start = Math.max(0, centerIndex - PRELOAD_BEHIND);
    const end = Math.min(images.length - 1, centerIndex + PRELOAD_AHEAD);
    
    for (let i = start; i <= end; i++) {
      if (!preloadCache.current[i] && images[i]) {
        const img = new Image();
        img.src = images[i].full;
        preloadCache.current[i] = img;
      }
    }
  }, [images]);

  // Check if image at index is fully loaded
  const isImageReady = (index) => {
    const cached = preloadCache.current[index];
    return cached && cached.complete && cached.naturalWidth > 0;
  };

  // Load image and return promise
  const loadImage = (index) => {
    return new Promise((resolve) => {
      if (isImageReady(index)) {
        resolve();
        return;
      }
      
      const img = new Image();
      img.onload = () => {
        preloadCache.current[index] = img;
        resolve();
      };
      img.onerror = () => resolve();
      img.src = images[index].full;
      preloadCache.current[index] = img;
    });
  };

  const openLightbox = async (index) => {
    setLightboxIndex(index);
    setLightboxLoading(true);
    
    // Start aggressive preload
    preloadLightboxRange(index);
    
    // Wait for current image
    await loadImage(index);
    setLightboxLoading(false);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setLightboxLoading(false);
  };

  const goToPrev = useCallback(async () => {
    if (lightboxLoading || lightboxIndex <= 0) return;
    
    const newIndex = lightboxIndex - 1;
    setLightboxLoading(true);
    setLightboxIndex(newIndex);
    
    // Preload extended range
    preloadLightboxRange(newIndex);
    
    await loadImage(newIndex);
    setLightboxLoading(false);
  }, [lightboxIndex, lightboxLoading, preloadLightboxRange]);

  const goToNext = useCallback(async () => {
    if (lightboxLoading || lightboxIndex >= images.length - 1) return;
    
    const newIndex = lightboxIndex + 1;
    setLightboxLoading(true);
    setLightboxIndex(newIndex);
    
    // Preload extended range
    preloadLightboxRange(newIndex);
    
    await loadImage(newIndex);
    setLightboxLoading(false);
  }, [lightboxIndex, lightboxLoading, images.length, preloadLightboxRange]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (lightboxLoading) return; // Block during loading
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, lightboxLoading, goToPrev, goToNext]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = lightboxIndex !== null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightboxIndex]);

  // Initial loading - just show spinner while fetching API
  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: '#0a1628',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}>
        <img
          src={VVS_LOGO}
          alt="VVS"
          style={{
            width: '80px',
            height: '80px',
            animation: 'vvs-spin 1.5s ease-in-out infinite',
          }}
        />
        <style>{`
          @keyframes vvs-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'relative', 
      zIndex: 1, 
      paddingTop: '70px',
      paddingBottom: '40px',
      minHeight: '100vh',
      background: '#0a1628',
    }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '0 12px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px', padding: '0 4px' }}>
          <Link
            to="/events/within"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: 'var(--vvs-muted)',
              textDecoration: 'none',
              fontSize: '10px',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontFamily: 'Space Grotesk',
              marginBottom: '16px',
            }}
          >
            <ChevronLeft size={12} /> Back to WITHIN
          </Link>

          <h1 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: 'clamp(28px, 5vw, 42px)',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '4px',
            letterSpacing: '-0.02em',
          }}>
            WITHIN
          </h1>
          <p style={{
            fontFamily: 'Space Grotesk',
            fontSize: '12px',
            letterSpacing: '0.08em',
            color: 'var(--vvs-cyan)',
          }}>
            26.02.2026 // Brussels Planetarium
          </p>
        </div>

        {/* Gallery Grid - Dense, tight spacing */}
        <div 
          className="gallery-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '3px',
          }}
        >
          {images.map((img, index) => (
            <GalleryImage 
              key={img.id}
              img={img}
              index={index}
              onClick={() => openLightbox(index)}
            />
          ))}
        </div>

        {/* Photo Credit */}
        <div style={{ 
          marginTop: '32px', 
          textAlign: 'center',
          padding: '16px 0',
        }}>
          <a
            href="https://instagram.com/christian.duta"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: 'Space Grotesk',
              fontSize: '12px',
              color: 'var(--vvs-muted)',
              textDecoration: 'none',
              transition: 'color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--vvs-cyan)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--vvs-muted)'}
            data-testid="photo-credit"
          >
            📸 @christian.duta
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.97)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: lightboxLoading ? 'wait' : 'default',
          }}
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          {/* Close button zone - 150x150px top right corner */}
          <div
            onMouseEnter={() => setCloseVisible(true)}
            onMouseLeave={() => setCloseVisible(false)}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              zIndex: 10002,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              padding: '20px',
            }}
          >
            <span
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              style={{
                color: '#ffffff',
                cursor: 'pointer',
                opacity: closeVisible ? 1 : 0,
                transition: 'opacity 0.2s ease',
                fontSize: '24px',
                fontWeight: 300,
                lineHeight: 1,
                userSelect: 'none',
              }}
              data-testid="lightbox-close"
            >
              ✕
            </span>
          </div>

          {/* Previous button */}
          {lightboxIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              disabled={lightboxLoading}
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: lightboxLoading ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
                border: 'none',
                color: lightboxLoading ? 'rgba(255,255,255,0.3)' : '#fff',
                cursor: lightboxLoading ? 'wait' : 'pointer',
                padding: '12px',
                borderRadius: '50%',
                zIndex: 10001,
                transition: 'background 0.15s, color 0.15s',
                pointerEvents: lightboxLoading ? 'none' : 'auto',
              }}
              onMouseEnter={e => !lightboxLoading && (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
              onMouseLeave={e => !lightboxLoading && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          {/* Spinner while loading */}
          {lightboxLoading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10002,
            }}>
              <img
                src={VVS_LOGO}
                alt="Loading"
                style={{
                  width: '60px',
                  height: '60px',
                  animation: 'vvs-spin 1.5s ease-in-out infinite',
                }}
              />
            </div>
          )}

          {/* Image */}
          <img
            src={images[lightboxIndex]?.full}
            alt={`WITHIN ${lightboxIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '92vw',
              maxHeight: '92vh',
              objectFit: 'contain',
              opacity: lightboxLoading ? 0 : 1,
              transition: 'opacity 0.1s',
            }}
          />

          {/* Next button */}
          {lightboxIndex < images.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              disabled={lightboxLoading}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: lightboxLoading ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
                border: 'none',
                color: lightboxLoading ? 'rgba(255,255,255,0.3)' : '#fff',
                cursor: lightboxLoading ? 'wait' : 'pointer',
                padding: '12px',
                borderRadius: '50%',
                zIndex: 10001,
                transition: 'background 0.15s, color 0.15s',
                pointerEvents: lightboxLoading ? 'none' : 'auto',
              }}
              onMouseEnter={e => !lightboxLoading && (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
              onMouseLeave={e => !lightboxLoading && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
              data-testid="lightbox-next"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      )}

      {/* Responsive styles */}
      <style>{`
        @keyframes vvs-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 1200px) {
          .gallery-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }
        @media (max-width: 900px) {
          .gallery-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 600px) {
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
}
